import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, Plus, Trash, PencilSimple, UploadSimple, CheckCircle } from "@phosphor-icons/react";
import { formatET } from "../lib/dateUtils";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";

const navItems = [{ to: "/admin/lead-packages", label: "Lead Packages", icon: <Package size={18} weight="duotone" />, testId: "nav-admin-lead-packages" }];

const emptyForm = () => ({ name: "", description: "", lead_count: 10, price: 0, product_interest: "any", active: true });

export default function AdminLeadPackagesPage() {
  const confirmEdit = useEditConfirm();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const fetchPackages = () => {
    api.get("/admin/lead-packages").then(({ data }) => setPackages(data));
  };

  const fetchOrders = () => {
    api.get("/admin/lead-packages/orders").then(({ data }) => setOrders(data)).catch(() => {});
  };

  useEffect(() => {
    fetchPackages();
    fetchOrders();
  }, []);

  const goUploadLeads = (order) => {
    const category = order.product_interest === "funding" ? "funding" : "trust";
    navigate(`/admin/users?upload_rep_id=${order.rep_id}&category=${category}`);
  };

  const toggleFulfilled = async (order) => {
    if (!(await confirmEdit(`${order.manager_name}'s order fulfillment status`))) return;
    try {
      await api.patch(`/admin/lead-packages/orders/${order.id}/fulfilled`, { fulfilled: !order.fulfilled });
      fetchOrders();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm({ name: pkg.name, description: pkg.description, lead_count: pkg.lead_count, price: pkg.price, product_interest: pkg.product_interest, active: pkg.active });
    setFormOpen(true);
  };

  const savePackage = async (e) => {
    e.preventDefault();
    if (editingId && !(await confirmEdit(`the "${form.name}" package`))) return;
    try {
      const payload = { ...form, lead_count: parseInt(form.lead_count) || 0, price: parseFloat(form.price) || 0 };
      if (editingId) {
        await api.patch(`/admin/lead-packages/${editingId}`, payload);
        toast.success("Package updated");
      } else {
        await api.post("/admin/lead-packages", payload);
        toast.success("Package created");
      }
      setFormOpen(false);
      fetchPackages();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleActive = async (pkg) => {
    if (!(await confirmEdit(`the "${pkg.name}" package (${pkg.active ? "deactivate" : "activate"})`))) return;
    try {
      await api.patch(`/admin/lead-packages/${pkg.id}`, { name: pkg.name, description: pkg.description, lead_count: pkg.lead_count, price: pkg.price, product_interest: pkg.product_interest, active: !pkg.active });
      fetchPackages();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deletePackage = async (id) => {
    if (!window.confirm("Delete this lead package?")) return;
    try {
      await api.delete(`/admin/lead-packages/${id}`);
      toast.success("Package deleted");
      fetchPackages();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Lead Packages" moduleBackPath="/admin">
      <div data-testid="admin-lead-packages-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="Bundle leads into priced packages that managers can purchase for their reps.">Lead Packages</h1>
            <p className="text-igc-muted text-sm">Foundation for bundling and pricing leads for future sale — not yet a live storefront.</p>
          </div>
          <Button data-testid="lead-package-add-button" onClick={openCreate} title="Creates a new lead package with its own price and lead count." className="bg-igc-gold hover:bg-igc-goldHover text-black flex items-center gap-2">
            <Plus size={16} weight="bold" /> New Package
          </Button>
        </div>

        {packages.length === 0 && <p className="text-igc-muted text-sm" data-testid="lead-packages-empty-state">No packages yet — create one to get started.</p>}

        <div className="space-y-2">
          {packages.map((p) => (
            <div key={p.id} className="bg-igc-surface border border-igc-gold/15 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`lead-package-row-${p.id}`}>
              <div className="flex-1 min-w-[220px]">
                <p className="text-igc-cream text-sm">{p.name} <span className="text-igc-goldText text-xs uppercase ml-1">{p.product_interest}</span></p>
                <p className="text-igc-muted text-xs">{p.lead_count} leads — ${p.price.toLocaleString()}{p.description ? ` — ${p.description}` : ""}</p>
              </div>
              <div className="flex items-center gap-3">
                <button data-testid={`lead-package-toggle-active-${p.id}`} onClick={() => toggleActive(p)} title="Turns this package on or off for purchase, without deleting it." className={`text-[10px] px-2 py-1 uppercase tracking-wide border ${p.active ? "bg-igc-success/10 text-igc-success border-igc-success/30" : "bg-igc-muted/10 text-igc-muted border-igc-muted/30"}`}>
                  {p.active ? "Active" : "Inactive"}
                </button>
                <button data-testid={`lead-package-edit-${p.id}`} onClick={() => openEdit(p)} title="Edit this package's name, price, description, or lead count." className="text-igc-muted hover:text-igc-goldText"><PencilSimple size={16} /></button>
                <button data-testid={`lead-package-delete-${p.id}`} onClick={() => deletePackage(p.id)} title="Permanently deletes this package." className="text-igc-danger hover:text-igc-danger/70"><Trash size={16} /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-1" title="Every manager who has purchased a package — fulfill it by uploading the actual leads to the rep's folder.">Purchase Orders</h2>
          <p className="text-igc-muted text-sm mb-4">Managers who've paid for a package — fulfill by uploading leads to the rep's folder.</p>
          {orders.length === 0 && <p className="text-igc-muted text-sm" data-testid="lead-package-orders-empty">No purchases yet.</p>}
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="bg-igc-surface border border-igc-gold/15 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`lead-package-order-row-${o.id}`}>
                <div>
                  <p className="text-igc-cream text-sm">{o.manager_name} ({o.manager_code}) — {o.package_name}</p>
                  <p className="text-igc-muted text-xs">{o.lead_count} leads — ${o.amount.toLocaleString()} — {formatET(o.created_at, "MMM d, yyyy h:mm a")}</p>
                  {o.rep_name && <p className="text-igc-goldText text-xs mt-1">For rep: {o.rep_name} ({o.rep_code})</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[10px] px-2 py-1 tracking-wide bg-igc-success/10 text-igc-success uppercase">Paid</span>
                  {o.rep_id && (
                    <Button data-testid={`lead-package-order-upload-${o.id}`} onClick={() => goUploadLeads(o)} title="Takes you to the Users page with this rep's folder ready for you to upload their leads." className="h-8 text-xs bg-igc-gold hover:bg-igc-goldHover text-black flex items-center gap-1.5">
                      <UploadSimple size={13} weight="bold" /> Upload Leads
                    </Button>
                  )}
                  <button
                    data-testid={`lead-package-order-fulfilled-toggle-${o.id}`}
                    onClick={() => toggleFulfilled(o)}
                    title="Marks whether this order's leads have actually been delivered to the rep."
                    className={`text-[10px] px-2 py-1.5 uppercase tracking-wide border flex items-center gap-1 ${o.fulfilled ? "bg-igc-success/10 text-igc-success border-igc-success/30" : "bg-igc-muted/10 text-igc-muted border-igc-muted/30"}`}
                  >
                    <CheckCircle size={12} weight={o.fulfilled ? "fill" : "regular"} /> {o.fulfilled ? "Fulfilled" : "Mark Fulfilled"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="lead-package-form-modal">
            <DialogHeader><DialogTitle className="font-serif">{editingId ? "Edit Package" : "New Package"}</DialogTitle></DialogHeader>
            <form onSubmit={savePackage} className="space-y-3">
              <Input data-testid="lead-package-name-input" required placeholder="Package name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <Textarea data-testid="lead-package-description-input" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="lead-package-count-input" type="number" min="1" required placeholder="Lead count" value={form.lead_count} onChange={(e) => setForm({ ...form, lead_count: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
                <Input data-testid="lead-package-price-input" type="number" min="0" required placeholder="Price ($)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20" />
              </div>
              <Select value={form.product_interest} onValueChange={(v) => setForm({ ...form, product_interest: v })}>
                <SelectTrigger data-testid="lead-package-product-select" className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="trust">Trust</SelectItem>
                  <SelectItem value="funding">Funding</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid="lead-package-submit-button" type="submit" className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">{editingId ? "Save Changes" : "Create Package"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
